import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomersAndSites, saveUser, SaveUserPayload, getUserById, getSalesDistrict, deleteUserById, getAccountsLite, CustomerAccountLite } from '../../Services/serviceURL';
import { useDispatch } from 'react-redux';
import { setCurrentUserId } from './usersSlice';
import SearchIcon from '../../Images/search-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import './AddUser.css';
import useSearch from '../../hooks/useSearch';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConfirmationModal from '../../Components/common/ConfirmationModal';
import Loader from '../../Components/common/loader/loader';

interface Plant {
    plantId: string;
    plantName: string;
}

interface Site {
    siteId: string;
    siteName: string;
    plants: Plant[];
}

interface CustomerData {
    customerId: string;
    customerName: string;
    soldTo: string;
    sitesAndPlants: Site[];
}

interface CustomerLite {
    customerId: string;
    customerName: string;
    soldTo: string;
}

interface AddUserFormData {
    userType: 'Internal' | 'External';
    email: string;
    ssoEnabled: boolean;
    password: string;
    firstName: string;
    lastName: string;
    mobilePhoneCode: string;
    mobilePhone: string;
    officePhoneCode: string;
    officePhone: string;
    role: string;
    status: 'Enabled' | 'Disabled';
    customerId?: string;
    salesDistrict?: string;
    salesRegion?: string;
}

interface FormErrors {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    customerId: string;
}

const AddUser: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userId } = useParams<{ userId: string }>();
    const isEditMode = Boolean(userId);

    const [customersAndSites, setCustomersAndSites] = useState<CustomerData[]>([]);
    const [selectedItems, setSelectedItems] = useState<{ [key: string]: boolean }>({});
    const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
    const [customers, setCustomers] = useState<CustomerLite[]>([]);
    const [formData, setFormData] = useState<AddUserFormData>({
        userType: 'Internal',
        email: '',
        password: '',
        ssoEnabled: false,
        firstName: '',
        lastName: '',
        mobilePhoneCode: '',
        mobilePhone: '',
        officePhoneCode: '',
        officePhone: '',
        role: 'User',
        status: 'Enabled',
        salesDistrict: '',
        salesRegion: ''
    });
    const [searchText, setSearchText] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState<CustomerData[]>([]);
    const [errors, setErrors] = useState<FormErrors>({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        customerId: ''
    });
    const [loading, setLoading] = useState(false);
    const [loadingUserData, setLoadingUserData] = useState(false);
    const [userDataLoaded, setUserDataLoaded] = useState(false);
    const [districts, setDistricts] = useState<{ id: number; salesRegionId: number; salesRegion: string; code: string; name: string; description: string; active: boolean }[]>([]);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [errorDistricts, setErrorDistricts] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<string | null>(null);
    const [showEncryptedPassword, setShowEncryptedPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { filteredData: filteredCustomersFromHook, handleSearchChange } = useSearch(customersAndSites);



    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                // Fetch customers using GetAccountsLite API endpoint
                const customerAccountsData = await getAccountsLite();
                
                
                // Ensure we have an array to work with
                if (!Array.isArray(customerAccountsData)) {
                    setCustomers([]);
                    return;
                }
                
                // Transform the API response to match CustomerLite interface
                const validCustomers = customerAccountsData.map((customer: CustomerAccountLite, index: number) => ({
                    customerId: customer.customerId,
                    customerName: customer.customerName,
                    soldTo: customer.salesDistrictCustomer || customer.salesDistrict || '' // Use salesDistrictCustomer as soldTo fallback
                }));
                
                setCustomers(validCustomers);
            } catch (error) {
                setCustomers([]);
            }
        };
        
        fetchCustomers();
    }, []);

    useEffect(() => {
        const fetchCustomersAndSites = async () => {
            try {
                // Fetch customers and sites data
                const response = await getCustomersAndSites();
                const data = response.Data || response;
                setCustomersAndSites(data);
            } catch (error) {
            }
        };
        
        fetchCustomersAndSites();
    }, []);

    useEffect(() => {
        setFilteredCustomers(filteredCustomersFromHook);
    }, [filteredCustomersFromHook]);

    // Load user data for edit mode
    useEffect(() => {
        if (isEditMode && userId && !userDataLoaded) {
            setLoadingUserData(true);
            // Fetch complete user data from API
            getUserById(userId)
                .then(response => {
                    if (response.Success && response.Data) {
                        const userData = response.Data;

                        const newFormData = {
                            userType: userData.type || 'Internal',
                            email: userData.email || '',
                            password: '', // Don't pre-populate password for security
                            ssoEnabled: userData.isSsoEnabled || false,
                            firstName: userData.firstName || '',
                            lastName: userData.lastName || '',
                            mobilePhoneCode: userData.mobilePhoneCode || '',
                            mobilePhone: userData.mobilePhoneNo || '',
                            officePhoneCode: userData.officePhoneCode || '',
                            officePhone: userData.officePhoneNo || '',
                            role: userData.role || 'User',
                            status: userData.status || 'Enabled',
                            customerId: userData.customerId || '',
                            salesDistrict: userData.salesDistrict || '',
                            salesRegion: userData.salesRegion || ''
                        };

                        setFormData(newFormData);
                        setUserDataLoaded(true);

                        // Show encrypted password placeholder if not SSO enabled
                        if (!newFormData.ssoEnabled) {
                            setShowEncryptedPassword(true);
                        }

                        // Set selected plant items for access visibility if available
                        if (userData.accessVisibility && Array.isArray(userData.accessVisibility)) {
                            const selectedPlants: { [key: string]: boolean } = {};
                            userData.accessVisibility.forEach((plantId: string) => {
                                selectedPlants[plantId] = true;
                            });
                            setSelectedItems(selectedPlants);
                        } else {
                            setSelectedItems({});
                        }
                    } else {
                        toast.error('User data not found');
                        navigate('/users');
                    }
                })
                .catch(error => {
                    toast.error('Failed to load user data');
                    navigate('/users');
                })
                .finally(() => {
                    setLoadingUserData(false);
                });
        }
    }, [isEditMode, userId, navigate, userDataLoaded]);


    // Update parent selections when customersAndSites data is loaded and we have selected items
    useEffect(() => {
        if (customersAndSites.length > 0 && Object.keys(selectedItems).length > 0) {
            // Only update if we don't already have parent selections
            const hasParentSelections = customersAndSites.some(customer =>
                selectedItems[customer.customerId] ||
                customer.sitesAndPlants.some(site => selectedItems[site.siteId])
            );

            if (!hasParentSelections) {
                updateParentSelections(selectedItems);
            }
        }
    }, [customersAndSites]);

    // Helper function to update parent selections based on selected plants
    const updateParentSelections = (selectedPlants: { [key: string]: boolean }) => {
        const newSelected = { ...selectedPlants };

        customersAndSites.forEach(customer => {
            customer.sitesAndPlants.forEach(site => {
                // Check if all plants in the site are selected
                const allPlantsSelected = site.plants.every(plant => selectedPlants[plant.plantId]);
                if (allPlantsSelected && site.plants.length > 0) {
                    newSelected[site.siteId] = true;
                }
            });

            // Check if all sites in the customer are selected
            const allSitesSelected = customer.sitesAndPlants.every(site => {
                const allPlantsInSite = site.plants.every(plant => selectedPlants[plant.plantId]);
                return allPlantsInSite && site.plants.length > 0;
            });
            if (allSitesSelected && customer.sitesAndPlants.length > 0) {
                newSelected[customer.customerId] = true;
            }
        });

        setSelectedItems(newSelected);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        // Clear field-specific errors when user starts typing
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // If password field is being edited, hide the encrypted placeholder
        if (name === 'password') {
            setShowEncryptedPassword(false);
            setShowPassword(false); // Also hide password when editing
        }

        // Restrict phone code fields to maximum 3 digits
        if (name === 'mobilePhoneCode' || name === 'officePhoneCode') {
            // Only allow digits and limit to 3 characters
            const numericValue = value.replace(/\D/g, ''); // Remove non-digits
            if (numericValue.length <= 3) {
                setFormData(prev => ({
                    ...prev,
                    [name]: numericValue
                }));
            }
            return;
        }

        // If SSO is being toggled, reset password states
        if (name === 'ssoEnabled') {
            setShowEncryptedPassword(false);
            setShowPassword(false);
            setFormData(prev => ({
                ...prev,
                ssoEnabled: value as unknown as boolean,
                password: '' // Clear password when SSO is toggled
            }));
            return;
        }

        // If district is changed, auto-fill salesRegion
        if (name === 'salesDistrict') {
            const selectedDistrict = districts.find(d => d.name === value);
            setFormData(prev => ({
                ...prev,
                salesDistrict: value,
                salesRegion: selectedDistrict ? selectedDistrict.salesRegion : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };
    const toggleExpand = (id: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            customerId: ''
        };

        let isValid = true;

        // Validate customer selection for External users
        if (formData.userType === 'External' && !formData.customerId) {
            newErrors.customerId = 'Please select a customer';
            isValid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'This field is required';
            isValid = false;
        }

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'This field is required';
            isValid = false;
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'This field is required';
            isValid = false;
        }
        // Password validation: required for new users, optional for editing existing users
        if (!formData.ssoEnabled && !formData.password.trim() && !isEditMode) {
            newErrors.password = 'This field is required';
            isValid = false;
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return; // Stop if validation fails
        }

        // Get selected plant IDs for accessVisibility
        const selectedPlantIds = Object.entries(selectedItems)
            .filter(([key, isSelected]) =>
                isSelected &&
                customersAndSites.some(c =>
                    c.sitesAndPlants.some(s =>
                        s.plants.some(p => p.plantId === key)
                    )
                )
            )
            .map(([key]) => key);

        // Prepare the payload according to the API structure
        const payload: SaveUserPayload = {
            id: isEditMode ? userId || "" : "", // Use userId for edit mode, empty for add mode
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.ssoEnabled ? '' : formData.password,
            mobilePhoneCode: formData.mobilePhoneCode,
            mobilePhoneNo: formData.mobilePhone,
            officePhoneCode: formData.officePhoneCode,
            officePhoneNo: formData.officePhone,
            type: formData.userType,
            role: formData.role,
            status: formData.status,
            isSsoEnabled: formData.ssoEnabled,
            accessVisibility: selectedPlantIds,
            // Only add customerId for External users
            customerId: formData.userType === 'External' ? formData.customerId : undefined,
            salesDistrict: formData.salesDistrict ?? '',
            salesRegion: formData.salesRegion ?? '',
        };

        try {
            setLoading(true);
            const response = await saveUser(payload, formData.userType);
            
            if (response.Success) {
                // Store the user ID in Redux here
                if (response.Data && response.Data.id) {
                    dispatch(setCurrentUserId(response.Data.id));
                }
                toast.success(`User ${isEditMode ? 'updated' : 'saved'} successfully`);
                // Add delay before navigation
                setTimeout(() => {
                    navigate('/users');
                }, 2000);
            } else {
                // Show specific API error message
                const errorMessage = response.Message || `Failed to ${isEditMode ? 'update' : 'save'} user`;
                toast.error(errorMessage);
                
                // Handle specific validation errors for form fields
                if (response.Message && response.Message.toLowerCase().includes('email already exists')) {
                    setErrors(prev => ({
                        ...prev,
                        email: 'This email address is already in use'
                    }));
                }
            }
        } catch (error: any) {
            // Extract error message from API response
            let errorMessage = `Failed to ${isEditMode ? 'update' : 'save'} user. Please try again.`;
            
            if (error.response && error.response.data) {
                // Check for backend error message
                if (error.response.data.Message) {
                    errorMessage = error.response.data.Message;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };
          // Helper function to check if item has content
         const hasSites = (customer: CustomerData) => customer.sitesAndPlants?.length > 0;
         const hasPlants = (site: Site) => site.plants?.length > 0;
    const handleSelectAll = (checked: boolean) => {
        const newSelected = { ...selectedItems };
        customersAndSites.forEach(customer => {
            newSelected[customer.customerId] = checked;
            customer.sitesAndPlants.forEach(site => {
                newSelected[site.siteId] = checked;
                site.plants.forEach(plant => {
                    newSelected[plant.plantId] = checked;
                });
            });
        });
        setSelectedItems(newSelected);
    };

    const handleCustomerSelect = (customerId: string, checked: boolean) => {
        const newSelected = { ...selectedItems };
        newSelected[customerId] = checked;

        const customer = customersAndSites.find(c => c.customerId === customerId);
        if (customer) {
            customer.sitesAndPlants.forEach(site => {
                newSelected[site.siteId] = checked;
                site.plants.forEach(plant => {
                    newSelected[plant.plantId] = checked;
                });
            });
        }
        setSelectedItems(newSelected);
    };

    const handleSiteSelect = (siteId: string, customerId: string, checked: boolean) => {
        const newSelected = { ...selectedItems };
        newSelected[siteId] = checked;

        const customer = customersAndSites.find(c => c.customerId === customerId);
        const site = customer?.sitesAndPlants.find(s => s.siteId === siteId);

        if (site) {
            // Update all child plants
            site.plants.forEach(plant => {
                newSelected[plant.plantId] = checked;
            });

            // Check if all sibling sites are selected
            if (customer) {
                const allSitesSelected = customer.sitesAndPlants.every(s =>
                    s.siteId === siteId ? checked : newSelected[s.siteId]
                );
                newSelected[customerId] = allSitesSelected;
            }
        }

        setSelectedItems(newSelected);
    };

    const handlePlantSelect = (plantId: string, siteId: string, customerId: string, checked: boolean) => {
        const newSelected = { ...selectedItems };
        newSelected[plantId] = checked;

        const customer = customersAndSites.find(c => c.customerId === customerId);
        const site = customer?.sitesAndPlants.find(s => s.siteId === siteId);

        if (site) {
            // Check if all plants in the site are selected
            const allPlantsSelected = site.plants.every(p =>
                p.plantId === plantId ? checked : newSelected[p.plantId]
            );
            newSelected[siteId] = allPlantsSelected;

            // Check if all sites in the customer are selected
            if (customer) {
                const allSitesSelected = customer.sitesAndPlants.every(s => {
                    if (s.siteId === siteId) {
                        return allPlantsSelected;
                    }
                    return newSelected[s.siteId];
                });
                newSelected[customerId] = allSitesSelected;
            }
        }

        setSelectedItems(newSelected);
    };

    const isCustomerSelected = (customerId: string) => selectedItems[customerId] || false;
    const isSiteSelected = (siteId: string) => selectedItems[siteId] || false;
    const isPlantSelected = (plantId: string) => selectedItems[plantId] || false;

    const getTotalCount = () => {
        return customersAndSites.reduce((total, customer) =>
            total + customer.sitesAndPlants.reduce((siteTotal, site) =>
                siteTotal + site.plants.length, 0), 0);
    };

    const getCustomerSiteCount = (customer: CustomerData) => {
    return customer.sitesAndPlants.length;
    };;

    const handleSearch = (searchValue: string) => {
        setSearchText(searchValue);

        if (!searchValue.trim()) {
            setFilteredCustomers(customersAndSites);
            return;
        }

        const searchLower = searchValue.toLowerCase();

        const filtered = customersAndSites.reduce<CustomerData[]>((acc, customer) => {
            // Check if customer name matches
            const customerMatches = customer.customerName.toLowerCase().includes(searchLower);

            // Filter sites and their plants
            const matchingSites = customer.sitesAndPlants.reduce<Site[]>((siteAcc, site) => {
                // Check if site name matches
                const siteMatches = site.siteName.toLowerCase().includes(searchLower);

                // Filter plants that match search term
                const matchingPlants = site.plants.filter(plant =>
                    plant.plantName.toLowerCase().includes(searchLower)
                );

                // Include site if it matches or has matching plants
                if (siteMatches || matchingPlants.length > 0) {
                    siteAcc.push({
                        ...site,
                        // If site matches, include ALL plants; otherwise only matching plants
                        plants: siteMatches ? site.plants : matchingPlants
                    });
                }
                return siteAcc;
            }, []);

            // Include customer if it matches or has matching sites
            if (customerMatches || matchingSites.length > 0) {
                acc.push({
                    ...customer,
                    // If customer matches, include ALL sites and plants; otherwise only matching sites
                    sitesAndPlants: customerMatches ? customer.sitesAndPlants : matchingSites
                });
            }
            return acc;
        }, []);

        setFilteredCustomers(filtered);

        // Auto-expand items with matches
        const newExpandedItems = { ...expandedItems };
        filtered.forEach(customer => {
            newExpandedItems[customer.customerId] = true;
            customer.sitesAndPlants.forEach(site => {
                newExpandedItems[site.siteId] = true;
            });
        });
        setExpandedItems(newExpandedItems);
    };

    // Fetch Sales Districts
    useEffect(() => {
        const fetchDistricts = async () => {
            setLoadingDistricts(true);
            setErrorDistricts(null);
            try {
                const response = await getSalesDistrict();
                if (response.data.Success && Array.isArray(response.data.Data)) {
                    setDistricts(response.data.Data);
                } else {
                    setDistricts([]);
                    setErrorDistricts('Failed to load districts');
                }
            } catch (error: any) {
                setDistricts([]);
                setErrorDistricts(error.message || 'Failed to load districts');
            } finally {
                setLoadingDistricts(false);
            }
        };
        fetchDistricts();
    }, []);

    const handleDelete = (userId: string) => {
        setUserToDelete(userId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            const response = await deleteUserById(userToDelete);
            if (response.Success) {
                setShowDeleteModal(false);
                setUserToDelete(null);
                navigate('/users', {
                    state: { userDeleted: true, message: 'User deleted successfully' },
                });
            } else {
                toast.error('Failed to delete user');
                setShowDeleteModal(false);
                setUserToDelete(null);
            }
        } catch (error) {
            toast.error('An error occurred while deleting user');
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
    };

    const visibleCustomers = React.useMemo(() => {
        if (formData.userType === 'External' && formData.customerId) {
            // Find the selected customer by customerId
            const filtered = customersAndSites.filter(
                c => c.customerId === formData.customerId
            );
            return filtered;
        }
        
        return customersAndSites;
    }, [formData.userType, formData.customerId, customersAndSites]);

    useEffect(() => {
    if (formData.userType === 'External') {
        // Only reset if we're NOT in edit mode (adding new user)
        // OR if user is switching from Internal to External
        if (!isEditMode || (formData.userType === 'External' && !formData.customerId)) {
            setExpandedItems({});
            setSelectedItems({});
        }
    }
}, [formData.customerId, formData.userType, isEditMode]);




    const isOverlayLoading = loading || loadingUserData || loadingDistricts;

    return (
        <div className="add-user-container" style={{ position: 'relative' }}>
            {isOverlayLoading && <Loader isOverlay={true} />}
            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ marginTop: '60px', zIndex: 9999 }}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                isDestructive={true}
            />
            <div className="page-header">
                <h2>{isEditMode ? 'Edit User' : 'Add User'}</h2>
                <div className="add-user-actions">
                    <button className="btn-cancel" onClick={() => navigate('/users')}>
                        Cancel
                    </button>
                    {isEditMode && (
                        <button
                            className="btn-delete-user"
                            onClick={() => userId && handleDelete(userId)}
                        >
                            Delete
                        </button>
                    )}
                    <button
                        className="btn-save"
                        onClick={handleSave}
                        disabled={loading || loadingUserData}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {loadingUserData ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    fontSize: '16px',
                    color: '#666'
                }}>
                    Loading user data... (Edit mode: {isEditMode ? 'true' : 'false'}, UserId: {userId})
                </div>
            ) : (
                <div className="form-sections-container">
                    {/* Left Column - User Information */}
                    <div className="form-section">
                        <h3 className="section-header">User Information</h3>

                        <div className="form-group">
                            <label>Select User Type</label>
                            <div className="user-type-options">
                                <button
                                    className={`user-type-button ${formData.userType === 'Internal' ? 'active' : ''}`}
                                    onClick={() => handleInputChange({ target: { name: 'userType', value: 'Internal' } } as any)}
                                >
                                    Internal
                                </button>
                                <button
                                    className={`user-type-button ${formData.userType === 'External' ? 'active' : ''}`}
                                    onClick={() => handleInputChange({ target: { name: 'userType', value: 'External' } } as any)}
                                >
                                    External
                                </button>
                            </div>
                            {formData.userType === 'External' && (
                                <div className="form-group">
                                    <label>Customer</label>
                                    <select
                                        name="customerId"
                                        value={formData.customerId || ''}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">-- Select Customer --</option>
                                        {customers.map((customer) => (
                                            <option key={customer.customerId} value={customer.customerId}>
                                                {customer.customerName}{customer.soldTo ? ` (#${customer.soldTo})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {formData.userType === 'External' && errors.customerId && (
                                <div className="error-message" style={{ color: 'red' }}>
                                    {errors.customerId}
                                </div>
                            )}

                        </div>


                        {/* Update email input section */}
                        <div className="form-group">
                            <div className="sso-toggle-container">
                                <span>SSO Enabled</span>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={formData.ssoEnabled}
                                        onChange={(e) => handleInputChange({
                                            target: { name: 'ssoEnabled', value: e.target.checked }
                                        } as any)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                            <label>Email Address <span className='required'>*</span></label>
                            <div className="email-container">
                                <div className="email-input">
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        className={errors.email ? 'error-input' : ''}
                                    />
                                    {errors.email && <span className="error-message-email">{errors.email}</span>}
                                </div>
                                {!formData.ssoEnabled && (

                                    <div className="password-input">
                                        <label>Password<span className='required'>*</span></label>
                                        <div className="password-input-container">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={showEncryptedPassword && !showPassword ? '••••••••••••' : formData.password}
                                                onChange={handleInputChange}
                                                placeholder={isEditMode ? "" : "Enter password"}
                                                className={errors.password ? 'error-input' : ''}
                                            />
                                            {!isEditMode && (
                                                <button
                                                    type="button"
                                                    className="password-toggle-btn"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: '#666'
                                                    }}
                                                >
                                                    {showPassword ? (
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M2 12C2 12 5 5 12 5s10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    ) : (
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M2 12C2 12 5 5 12 5s10 7 10 7-3 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {errors.password && <span className="error-message">{errors.password}</span>}
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* First Name and Last Name in separate rows */}
                        <div className="form-group">
                            <label>First Name<span className='required'>*</span></label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                placeholder="Enter first name"
                                className={errors.firstName ? 'error-input' : ''}
                            />
                            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                        </div>

                        <div className="form-group">
                            <label>Last Name<span className='required'>*</span></label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                placeholder="Enter last name"
                                className={errors.lastName ? 'error-input' : ''}
                            />
                            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                        </div>

                        {/* Sales District and Sales Region for Internal user only */}
                        {/* {formData.userType === 'Internal' && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="salesDistrict">Sales District</label>
                                    <select
                                        id="salesDistrict"
                                        name="salesDistrict"
                                        value={formData.salesDistrict || ''}
                                        onChange={handleInputChange}
                                        disabled={loadingDistricts}
                                    >
                                        <option value="">Select District</option>
                                        {districts.map(d => (
                                            <option key={d.id} value={d.name}>{d.name}</option>
                                        ))}
                                    </select>
                                    {errorDistricts && <span className="error-text">{errorDistricts}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="salesRegion">Sales Region</label>
                                    <input
                                        type="text"
                                        id="salesRegion"
                                        name="salesRegion"
                                        value={formData.salesRegion || ''}
                                        onChange={handleInputChange}
                                        placeholder="Enter sales region"
                                    />
                                </div>
                            </>
                        )} */}

                        {/* Phone Input Groups */}
                        <div className="form-group phone-form-group">
                            <label>Mobile Phone Number</label>
                            <div className="phone-input-group">
                                <input
                                    type="text"
                                    className="phone-code"
                                    name="mobilePhoneCode"
                                    value={formData.mobilePhoneCode}
                                    onChange={handleInputChange}
                                    placeholder="+1"
                                    maxLength={3}
                                />
                                <input
                                    type="tel"
                                    className="phone-number"
                                    name="mobilePhone"
                                    value={formData.mobilePhone}
                                    onChange={handleInputChange}
                                    placeholder="(123) 456-7890"
                                />
                            </div>
                        </div>

                        <div className="form-group phone-form-group">
                            <label>Office Phone Number</label>
                            <div className="phone-input-group">
                                <input
                                    type="text"
                                    className="phone-code"
                                    name="officePhoneCode"
                                    value={formData.officePhoneCode}
                                    onChange={handleInputChange}
                                    placeholder="+1"
                                    maxLength={3}
                                />
                                <input
                                    type="tel"
                                    className="phone-number"
                                    name="officePhone"
                                    value={formData.officePhone}
                                    onChange={handleInputChange}
                                    placeholder="(123) 456-7890"
                                />
                            </div>
                        </div>
                        <div className="form-row">
                        <div className="form-group">
                        <label>Access Level</label>
                        <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                        >
                        {formData.userType === 'Internal' ? (
                        // Internal users can choose between Admin and User
                        <>
                        <option value="Admin">Admin</option>
                        <option value="User">User</option>
                        </>
                        ) : (
                        // External users can only be User
                        <option value="User">User</option>
                        )}
                        </select>

                        </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className='user-status'>Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                >
                                    <option value="Enabled">Enabled</option>
                                    <option value="Disabled">Disabled</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Asset Visibility */}
                    <div className="form-section">
                        <h3 className="section-header">Asset Visibility</h3>
                        <div className="search-container">
                            <img src={SearchIcon} alt="search" className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by customer, site or plant"
                                className="search-input"
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>

                        <div className="visibility-options-scroll-container">
                            <div className="visibility-options">
                                {formData.userType === 'Internal' && (
                                    <>
                                        <label className="checkbox-container">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                            <span className="checkbox-label">All Customers & Sites</span>
                                            <div className="item-controls">
                                                <span className="item-count">{getTotalCount()}</span>
                                            </div>
                                        </label>
                                        <div className='checkbox-label-all-divider'></div>
                                    </>
                                )}

                                {/* {(searchText ? filteredCustomers : customersAndSites).map((customer) => ( */}
 
                             {(searchText ? filteredCustomers : visibleCustomers).map((customer) => {
                              // Check if customer has ANY plants at all
                              const hasAnyPlants = customer.sitesAndPlants.some(site => 
                              site.plants && site.plants.length > 0
                              );
  
                              // Calculate total plant count for display
                               const totalPlantCount = customer.sitesAndPlants.reduce((total, site) => 
                                total + (site.plants?.length || 0), 0
                                );
  
                               const customerTooltipText = !hasAnyPlants ? "No plants available for selection" : "";

                      return (
                       <div key={customer.customerId} className="customer-section">
                           {/* Customer Level */}
                         <div className="checkbox-container">
                         {/* ALWAYS DISABLE CHECKBOX IF NO PLANTS */}
                           <input
                            type="checkbox"
                        checked={hasAnyPlants ? isCustomerSelected(customer.customerId) : false}
                        onChange={(e) => {
                         if (hasAnyPlants) {
                           handleCustomerSelect(customer.customerId, e.target.checked);
                           }
                           }}
                           disabled={!hasAnyPlants}  // DISABLE when no plants
                          title={customerTooltipText}  
                            />
                            <span className={`checkbox-label ${!hasAnyPlants ? 'disabled' : ''}`}>
                           {customer.customerName}
                           {customer.soldTo ? ` (#${customer.soldTo})` : ''}
                            </span>
                           <div className="item-controls">
                           <span className="item-count" title={customerTooltipText}>
                            {totalPlantCount}
                            </span>
                         {/* Only show expand button if customer has sites */}
                         {customer.sitesAndPlants.length > 0 ? (
                         <button
                            className="expand-button"
                            onClick={(e) => {
                           e.preventDefault();
                          toggleExpand(customer.customerId);
                          }}
                         >
                             {expandedItems[customer.customerId] ? (
                            <img className="arrow-up" src={ArrowUp} alt="sort" />
                             ) : (
                           <img className="arrow-down" src={ArrowDown} alt="sort" />
                           )}
                            </button>
                          ) : (
                       <div className="expand-button-placeholder"></div>
                   )}
                </div>
               </div>

      {/* Sites Level - Only show if customer has sites and is expanded */}
      {customer.sitesAndPlants.length > 0 && expandedItems[customer.customerId] && (
        <div className="site-list">
          {customer.sitesAndPlants.map((site) => {
            const plantCount = site.plants?.length || 0;
            const hasPlants = plantCount > 0;
            const siteTooltipText = !hasPlants ? "No plants available for selection" : "";
            
            return (
              <div key={site.siteId}>
                {/* Site Level */}
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={hasPlants ? isSiteSelected(site.siteId) : false}
                    onChange={(e) => {
                      if (hasPlants) {
                        handleSiteSelect(site.siteId, customer.customerId, e.target.checked);
                      }
                    }}
                    disabled={!hasPlants}
                    title={siteTooltipText} 
                  />
                  <span className={`checkbox-label ${!hasPlants ? 'disabled' : ''}`}>
                    {site.siteName}
                  </span>
                  <div className="item-controls">
                    <span className="item-count" title={siteTooltipText}>
                      {plantCount}
                    </span>
                    {hasPlants ? (
                      <button
                        className="expand-button"
                        onClick={(e) => {
                          e.preventDefault();
                          toggleExpand(site.siteId);
                        }}
                      >
                        {expandedItems[site.siteId] ? (
                          <img className="arrow-up" src={ArrowUp} alt="sort" />
                        ) : (
                          <img className="arrow-down" src={ArrowDown} alt="sort" />
                        )}
                      </button>
                    ) : (
                      <div className="expand-button-placeholder"></div>
                    )}
                  </div>
                </div>

                {/* Plants Level - Only show if site has plants and is expanded */}
                {hasPlants && expandedItems[site.siteId] && (
                  <div className="plant-list">
                    {site.plants.map((plant) => (
                      <label key={plant.plantId} className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={isPlantSelected(plant.plantId)}
                          onChange={(e) => handlePlantSelect(plant.plantId, site.siteId, customer.customerId, e.target.checked)}
                        />
                        <span className="checkbox-label">{plant.plantName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
})}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddUser;